import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LangContext";
import { DISHES, PRICE } from "@/data/site";
import { Reveal, Kicker } from "./Reveal";
import { openWhatsApp } from "@/lib/wa";

export default function MenuSection() {
  const { t, lang } = useLang();

  return (
    <section id="menu" className="relative py-24 md:py-36" data-testid="menu-section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div>
            <Kicker className="mb-5">{t.menuKicker}</Kicker>
            <h2 className="font-serif text-5xl leading-[0.95] tracking-tight text-[var(--text)] md:text-7xl">
              {t.menuTitle} <br />
              <span className="italic gold-gradient-text">{t.menuTitleEm}</span>
            </h2>
          </div>
          <Reveal className="max-w-sm" delay={0.15}>
            <p className="font-sans text-[15px] leading-relaxed text-[var(--text-2)]">{t.menuIntro}</p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {DISHES.map((d, i) => {
            return (
              <motion.article
                key={d.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group relative flex flex-col overflow-hidden border border-[var(--gold)]/15 bg-[var(--bg-2)]"
                data-testid={`dish-card-${d.id}`}
              >
                <div className="relative overflow-hidden bg-[#0c0c0c] aspect-[4/5]">
                  <img
                    src={d.img}
                    alt={d.name}
                    loading="lazy"
                    className="h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                  />
                  <span className="absolute left-4 top-4 rounded-full border border-[var(--gold)]/50 bg-black/50 px-3 py-1 font-sans text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] backdrop-blur">
                    {d.tag}
                  </span>
                  <span className="absolute right-4 top-4 bg-[var(--gold)] px-3.5 py-1.5 font-serif text-xl font-bold leading-none text-[var(--bg)] shadow-lg">
                    {d.price || PRICE}
                  </span>
                </div>
                <div className="flex flex-1 flex-col justify-between gap-4 p-6">
                  <div>
                    <h3 className="font-serif text-2xl leading-tight tracking-tight text-[var(--text)]">
                      {d.name}
                    </h3>
                    <p className="mt-3 font-sans text-sm leading-relaxed text-[var(--text-2)]">
                      {lang === "fr" ? d.fr : d.en}
                    </p>
                  </div>
                  <button
                    onClick={() => openWhatsApp(`${t.waOrder} (${d.name})`)}
                    data-testid={`dish-order-${d.id}`}
                    className="self-start font-sans text-xs uppercase tracking-[0.22em] text-[var(--gold)] transition-colors hover:text-[var(--gold-light)]"
                  >
                    {t.heroCta} →
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
